import {Box, Col, ColRow, Flex, Grid} from '@indoqa/style-system'
import {IStyle} from 'fela'
import * as React from 'react'
import {useFela} from 'react-fela'
import {authenticator, Permission} from '../../../app/auth/SessionPlusAuthenticator'
import {Theme} from '../../../app/theme'
import {arPathBook, arPathContentResources, arPathSearch} from '../../store/ar.paths'
import {ArAnsprechpartnerSection} from '../contact/ArAnsprechpartnerSection'
import {useKiara} from '../kiara/KiaraContext'
import {ArLink} from '../links/ArLink'
import {ArSearchInput} from '../search/ArSearchInput'
import {ArDivider} from './ArDivider'
import {ArOverviewMarketingPanel} from './ArOverviewMarketingPanel'
import {ArServiceButton} from './ArServiceButton'
import {ArTopicTile} from './ArTopicTile'

const KONTAKT_LINK = 'https://arbeitsrecht-fuer-betriebsraete.at/kontakt/'
const BESTELLEN_LINK = 'https://shop.oegbverlag.at/arbeitsrecht-fuer-betriebsraete-plus'
const SOFT_HYPHEN = String.fromCharCode(173)
const SERVICE_BUTTON_GAP = 40

const TOPICS = [
  {name: 'Pflegefreistellung', query: 'pflegefreistellung'},
  {name: 'Betriebsratsfonds', query: 'Betriebsratsfonds'},
  {name: `Arbeitskräfte${SOFT_HYPHEN}überlassung`, query: 'Arbeitskräfteüberlassung'},
  {name: 'Datenschutz', query: 'Datenschutz'},
  {name: 'Lohn- und Sozialdumping', query: 'Lohn- und Sozialdumping'},
  {name: 'Abfertigung', query: 'Abfertigung'},
]

export const ArOverviewPanel = () => {
  const {closeKiara, kiaraEnabled} = useKiara()
  const {css, theme} = useFela<Theme>()

  const serviceButtonsStyle: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.space4,
    paddingTop: theme.spacing.space4,
    tablet: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'stretch',
      gap: SERVICE_BUTTON_GAP,
    },
  }
  const kiaraLogoStyle: IStyle = {
    width: '100%',
    maxWidth: 240,
    margin: 0,
    marginRight: theme.spacing.space6,
  }

  if (!authenticator.isAuthenticated()) {
    return <ArOverviewMarketingPanel />
  }

  const isPlusUser = authenticator.hasPermission(Permission.ARFBR_PLUS)
  const isPremiumUser = authenticator.hasPermission(Permission.ARFBR_PREMIUM)
  const isArfbrUser = isPlusUser || isPremiumUser

  const leitfadenLink = isArfbrUser
    ? arPathBook(
        'Leitfaden Betriebsvereinbarung',
        '351877-0.1',
        '351877-0.1_pr351886_3007050',
        '51877-0.1_pr351886_3007050'
      )
    : arPathSearch('Leitfaden Betriebsvereinbarung')

  return (
    <Box fullWidth mt={16} mb={20}>
      <Flex justifyContent="center" fullWidth direction="column">
        <Box textAlign="center">
          <h1>Arbeitsrecht für Betriebsräte</h1>
        </Box>
        <Box textAlign="center">
          <p>Arbeitsrechtliche Fragen als Betriebsrat einfach und schnell lösen!</p>
        </Box>

        <Flex fullWidth center py={3}>
          <Box maxWidth={750} fullWidth>
            <ArSearchInput />
          </Box>
        </Flex>

        <Flex center>
          <ArServiceButton
            variant="filled"
            compact
            text="Alle Inhalte"
            link={arPathContentResources()}
            onClick={closeKiara}
          />
        </Flex>
      </Flex>

      <Box pt={8}>
        {kiaraEnabled ? (
          <p>
            Entdecke alle arbeitsrechtlichen Fachinhalte, suche nach einem Arbeitsrechtsthema oder frage KIARA, den
            integrierten KI-Chatbot des ÖGB-Verlags.
          </p>
        ) : (
          <p>Entdecke alle arbeitsrechtlichen Fachinhalte oder suche nach einem Arbeitsrechtsthema.</p>
        )}
      </Box>

      <Box pt={8}>
        <h2>Häufig gesuchte Themen</h2>
      </Box>
      <Grid spacing={theme.spacing.space4} width="100%">
        <ColRow>
          {TOPICS.map((topic) => (
            <Col key={topic.query} size={[12, 4]}>
              <ArTopicTile name={topic.name} link={arPathSearch(topic.query)} />
            </Col>
          ))}
        </ColRow>
      </Grid>

      <Box pt={8}>
        <h3>Leitfaden Betriebsvereinbarungen</h3>
      </Box>
      <Box>
        <p>
          Betriebsvereinbarungen sind das zentrale Instrument zur Mitbestimmung im Betrieb. Im „
          <ArLink name="Leitfaden Betriebsvereinbarungen" link={leitfadenLink} />“ findest du alle Infos zu den
          unterschiedlichen Arten von Betriebsvereinbarungen und was du beim Abschluss einer Betriebsvereinbarung
          beachten solltest.
        </p>
      </Box>
      <ArDivider />

      {kiaraEnabled && (
        <>
          <Flex pt={8} alignItems="center">
            <img src={`${process.env.PUBLIC_URL}/kiara-logo.png`} alt="KIARA" className={css(kiaraLogoStyle)} />
            <h2>NEU: KIARA, die künstliche Intelligenz für Arbeitsrecht</h2>
          </Flex>
          <Box mt={4}>
            <p>
              Mit <strong>KIARA, dem KI-Chatbot für Arbeitsrecht des ÖGB-Verlags</strong>, kannst du das digitale
              Fachwissen in Arbeitsrecht für Betriebsräte jetzt auch direkt befragen: Stelle deine{' '}
              <strong>arbeitsrechtliche Frage</strong> in eigenen Worten.
            </p>
            <p>
              KIARA liefert dir innerhalb weniger Sekunden eine <strong>klare und verständliche Antwort</strong> aus dem
              Arbeitsrechtswissen des ÖGB-Verlags, inklusive Quellenverlinkungen.
            </p>
          </Box>
          <ArDivider />
        </>
      )}

      <Box pt={8}>
        <h2>Service</h2>
      </Box>
      <div className={css(serviceButtonsStyle)}>
        <ArServiceButton variant="filled" text="Arbeitsrecht für Betriebsräte bestellen" link={BESTELLEN_LINK} />
        <ArServiceButton variant="outlined" text="Online-Präsentation" link={KONTAKT_LINK} />
      </div>

      <ArAnsprechpartnerSection intro="Du hast Fragen zum Produkt? Wende dich an deinen persönlichen Ansprechpartner:" />
    </Box>
  )
}
